import {
  useMemo,
  useState,
} from "react";

import {
  buildCombinedCsv,
  buildIndividualCsv,
  buildSummaryCsv,
  downloadText,
  individualCsvName,
} from "./api/csv";


const MAX_FILES = 100;


export default function BatchExamPage({
  questionCount,
  title,
  description,
  submitFunction,
}) {
  const [files, setFiles] = useState([]);
  const [answerKey, setAnswerKey] = useState("");
  const [threshold, setThreshold] = useState("");
  const [forceOneAnswer, setForceOneAnswer] =
    useState(false);

  const [response, setResponse] =
    useState(null);
  const [loading, setLoading] =
    useState(false);
  const [error, setError] =
    useState("");

  const [completedFiles, setCompletedFiles] =
    useState(0);
  const [currentFilename, setCurrentFilename] =
    useState("");

  const answerCount = useMemo(
    () =>
      (
        answerKey
          .toUpperCase()
          .match(/\b[ABCD]\b/g)
        || []
      ).length,
    [answerKey]
  );


  function resetProgress() {
    setCompletedFiles(0);
    setCurrentFilename("");
  }


  function handleFiles(event) {
    const selected = Array.from(
      event.target.files || []
    );

    setError("");
    setResponse(null);
    resetProgress();

    if (selected.length > MAX_FILES) {
      setFiles([]);
      setError(
        `Select at most ${MAX_FILES} answer sheets.`
      );
      event.target.value = "";
      return;
    }

    const invalid = selected.find(
      (file) =>
        ![
          "image/jpeg",
          "image/png",
          "image/webp",
        ].includes(file.type)
    );

    if (invalid) {
      setFiles([]);
      setError(
        `Unsupported file: ${invalid.name}`
      );
      event.target.value = "";
      return;
    }

    setFiles(selected);
  }


  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setResponse(null);
    resetProgress();

    if (files.length === 0) {
      setError(
        "Select at least one answer sheet."
      );
      return;
    }

    if (answerCount !== questionCount) {
      setError(
        `This page requires exactly `
        + `${questionCount} answer-key entries. `
        + `Found ${answerCount}.`
      );
      return;
    }

    try {
      setLoading(true);

      const data = await submitFunction({
        files,
        answerKey,
        threshold,
        forceOneAnswer,

        onProgress: ({
          completed,
          filename,
        }) => {
          setCompletedFiles(completed);
          setCurrentFilename(filename);
        },
      });

      // Results are displayed only after all selected
      // files have completed.
      setResponse(data);

    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : String(requestError)
      );

    } finally {
      setLoading(false);
      setCurrentFilename("");
    }
  }


  function downloadSummary() {
    downloadText(
      `omr_${questionCount}_question_summary.csv`,
      buildSummaryCsv(
        response.results
      )
    );
  }


  function downloadAllDetails() {
    downloadText(
      `omr_${questionCount}_question_details.csv`,
      buildCombinedCsv(
        response.results
      )
    );
  }


  return (
    <main className="page">
      <section className="panel">
        <div className="exam-badge">
          {questionCount} questions
        </div>

        <h1>{title}</h1>
        <p>{description}</p>

        <form onSubmit={handleSubmit}>
          <label className="field">
            <span>
              Answer sheets (
              {files.length}/{MAX_FILES})
            </span>

            <input
              type="file"
              accept={
                "image/jpeg,"
                + "image/png,"
                + "image/webp"
              }
              multiple
              disabled={loading}
              onChange={handleFiles}
            />
          </label>

          {files.length > 0 && (
            <div className="selected-files">
              {files.map(
                (file, index) => (
                  <span
                    className="file-chip"
                    key={
                      file.name
                      + index
                    }
                  >
                    {index + 1}.{" "}
                    {file.name}
                  </span>
                )
              )}
            </div>
          )}

          <label className="field">
            <span>
              Answer key (
              {answerCount}/
              {questionCount})
            </span>

            <textarea
              rows="8"
              value={answerKey}
              disabled={loading}
              onChange={
                (event) =>
                  setAnswerKey(
                    event.target.value
                  )
              }
              placeholder={
                questionCount === 50
                  ? "Enter exactly 50 answers: A,B,C,D,..."
                  : "Enter exactly 100 answers: A,B,C,D,..."
              }
            />
          </label>

          {/* <label className="checkbox-field">
            <input
              type="checkbox"
              checked={forceOneAnswer}
              disabled={loading}
              onChange={
                (event) =>
                  setForceOneAnswer(
                    event.target.checked
                  )
              }
            />

            <span>
              Every question must have
              one answer
            </span>
          </label> */}

          {/* <label className="field">
            <span>
              Manual threshold —
              optional
            </span>

            <input
              type="number"
              min="0"
              step="0.1"
              value={threshold}
              disabled={loading}
              onChange={
                (event) =>
                  setThreshold(
                    event.target.value
                  )
              }
              placeholder="Automatic"
            />
          </label> */}

          {loading && (
            <div className="processing-progress">
              <div>
                Processing{" "}
                <strong>
                  {completedFiles}/
                  {files.length}
                </strong>
              </div>

              <progress
                value={completedFiles}
                max={files.length}
              />

              {currentFilename && (
                <small>
                  Current file:{" "}
                  {currentFilename}
                </small>
              )}
            </div>
          )}

          <button
            className="primary-button"
            disabled={loading}
          >
            {loading
              ? `Processing ${completedFiles}/${files.length}...`
              : `Check ${questionCount}-question sheets`}
          </button>
        </form>

        {error && (
          <div className="error-box">
            {error}
          </div>
        )}
      </section>

      {response && (
        <>
          <BatchSummary
            response={response}
          />

          <section className="panel action-panel">
            <button
              type="button"
              onClick={downloadSummary}
            >
              Download summary
            </button>

            <button
              type="button"
              onClick={
                downloadAllDetails
              }
            >
              Download all details
            </button>
          </section>

          <section className="results-grid">
            {response.results.map(
              (sheet) => (
                <SheetCard
                  key={
                    sheet.id
                    + sheet.filename
                  }
                  sheet={sheet}
                />
              )
            )}
          </section>
        </>
      )}
    </main>
  );
}


function BatchSummary({ response }) {
  const summary =
    response.batchSummary;

  return (
    <section className="panel">
      <h2>
        {response.examQuestionCount}
        -question batch summary
      </h2>

      <div className="summary-grid">
        <SummaryCard
          label="Uploaded"
          value={summary.uploaded}
        />
        <SummaryCard
          label="Processed"
          value={summary.processed}
        />
        <SummaryCard
          label="Failed"
          value={summary.failed}
        />
        {/* <SummaryCard
          label="Total correct"
          value={summary.totalCorrect}
        />
        <SummaryCard
          label="Average"
          value={
            summary.averagePercentage
            + "%"
          }
        /> */}
      </div>
    </section>
  );
}


function SummaryCard({
  label,
  value,
}) {
  return (
    <article className="summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}


function SheetCard({ sheet }) {
  function downloadIndividual() {
    downloadText(
      individualCsvName(sheet),
      buildIndividualCsv(sheet)
    );
  }

  if (!sheet.success) {
    return (
      <article className="sheet-card failed-card">
        <h2>{sheet.filename}</h2>
        <strong>
          Processing failed
        </strong>
        <p>{sheet.error}</p>
      </article>
    );
  }

  return (
    <article className="sheet-card">
      <header className="sheet-heading">
        <div>
          <h2>{sheet.filename}</h2>

          <p>
            Score:{" "}
            <strong>
              {sheet.summary.correct}/
              {sheet.summary.total}
            </strong>{" "}
            (
            {sheet.summary.percentage}
            %)
          </p>
        </div>

        <button
          type="button"
          className="small-button"
          onClick={
            downloadIndividual
          }
        >
          Download
        </button>
      </header>

      <div className="mini-summary">
        <span>
          Correct:{" "}
          {sheet.summary.correct}
        </span>
        <span>
          Wrong:{" "}
          {sheet.summary.wrong}
        </span>
        <span>
          Blank:{" "}
          {sheet.summary.blank}
        </span>
        <span>
          Multiple:{" "}
          {sheet.summary.multiple}
        </span>
        <span>
          Total Marks Obtained:{" "}
          {((Number(sheet.summary.correct)-(Number(sheet.summary.wrong)+Number(sheet.summary.multiple))*0.2)*100/Number(sheet.summary.total)).toFixed(2)}
        </span>
      </div>

      <img
        className="annotated-image"
        src={sheet.annotatedImage}
        alt={
          "Annotated result for "
          + sheet.filename
        }
      />

      <details>
        <summary>
          Show all{" "}
          {sheet.summary.total}{" "}
          question results
        </summary>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Q.</th>
                <th>Detected</th>
                <th>Correct</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {sheet.questions.map(
                (question) => (
                  <tr
                    key={
                      question.question
                    }
                  >
                    <td>
                      {question.question}
                    </td>
                    <td>
                      {
                        question.detectedAnswer
                      }
                    </td>
                    <td>
                      {
                        question.correctAnswer
                      }
                    </td>
                    <td>
                      <span
                        className={
                          "status "
                          + "status-"
                          + question.status
                        }
                      >
                        {question.status}
                      </span>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </details>
    </article>
  );
}
